import React, { useState, useRef } from 'react';
import { ImportType } from '../../types/csvImport';

interface CSVImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  importType: ImportType;
  onImport: (file: File) => void;
  isImporting: boolean;
}

const CSVImportModal: React.FC<CSVImportModalProps> = ({
  isOpen,
  onClose,
  importType,
  onImport,
  isImporting
}) => {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        onImport(file);
      } else {
        alert('Please select a CSV file');
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      onImport(file);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const getImportTypeInfo = () => {
    switch (importType) {
      case ImportType.FUNCTIONS:
        return {
          title: 'Import Critical Functions',
          description: 'Upload a CSV file containing critical business functions data',
          requiredFields: ['name', 'description', 'criticality', 'max_downtime', 'responsible_role', 'responsible_team', 'rto', 'rpo', 'owner'],
          sampleData: 'Customer Service Operations,Handling customer inquiries,critical,2 hours,Customer Service Manager,Customer Service,Phone system;CRM,IT systems;Staff,Activate backup call center,1 hour,15 minutes,John Smith'
        };
      case ImportType.SUPPLIERS:
        return {
          title: 'Import Key Suppliers',
          description: 'Upload a CSV file containing key suppliers data',
          requiredFields: ['name'],
          sampleData: 'ABC IT Services,Jane Doe,+1-555-0123,jane@abcitservices.com,IT support and maintenance,high,XYZ Tech;IT Solutions Inc,4 hours,high'
        };
      case ImportType.IT_SYSTEMS:
        return {
          title: 'Import IT Systems',
          description: 'Upload a CSV file containing IT systems data',
          requiredFields: ['name'],
          sampleData: 'Customer Database,Primary customer database,critical,1 hour,IT Operations,AWS RDS Multi-AZ,Failover to standby,30 minutes,5 minutes,database,Amazon Web Services,Customer Service;Sales,Network;Auth,1'
        };
      default:
        return {
          title: 'Import Data',
          description: 'Upload a CSV file',
          requiredFields: [],
          sampleData: ''
        };
    }
  };

  const info = getImportTypeInfo();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="inline-block w-full max-w-2xl p-6 my-8 text-left align-middle bg-white rounded-2xl shadow-xl transition-all transform">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {info.title}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isImporting}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-6">
            <div>
              <p className="text-sm text-gray-600 mb-4">
                {info.description}
              </p>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Required Fields:</h4>
                <div className="flex flex-wrap gap-2">
                  {info.requiredFields.map((field, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded"
                    >
                      {field}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* File Upload Area */}
            <div
              className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={isImporting}
              />

              <div className="space-y-4">
                <div className="mx-auto w-12 h-12 text-gray-400">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>

                <div>
                  <p className="text-lg font-medium text-gray-900">
                    {isImporting ? 'Processing...' : 'Drop your CSV file here'}
                  </p>
                  <p className="text-sm text-gray-500">
                    or <button
                      type="button"
                      onClick={handleClick}
                      className="text-blue-600 hover:text-blue-500 font-medium"
                      disabled={isImporting}
                    >
                      browse files
                    </button>
                  </p>
                </div>
              </div>
            </div>

            {/* Sample Data */}
            {info.sampleData && (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Sample CSV Format:</h4>
                <div className="text-xs text-gray-600 font-mono bg-white p-2 rounded border overflow-x-auto">
                  {info.sampleData}
                </div>
              </div>
            )}

            {/* Import Status */}
            {isImporting && (
              <div className="flex items-center justify-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center space-x-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <span className="text-sm text-blue-700">Importing data...</span>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                disabled={isImporting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CSVImportModal;
